import React, { useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Tooltip, Typography, message as antdMessage } from 'antd';
import { QualityDocumentUpdateRequestPayload } from '../../../types/types';
import type { QualityDocumentRow } from './index';

const { Paragraph, Text } = Typography;

type QualityDocumentRequestType = 'update' | 'help';

interface RequestDocumentUpdateModalProps {
    open: boolean;
    order: any;
    documents: QualityDocumentRow[];
    onCancel: () => void;
    onSubmit: (payload: QualityDocumentUpdateRequestPayload) => Promise<void>;
}

const getRequestTypeForDocument = (document: QualityDocumentRow): QualityDocumentRequestType =>
    document.status === 'approved' ? 'help' : 'update';

const getRequestTypeLabel = (requestType: QualityDocumentRequestType) =>
    requestType === 'help' ? 'Need help' : 'Need update';

const getSelectedCountLabel = (count: number) => `${count} selected`;

const RequestDocumentUpdateModal: React.FC<RequestDocumentUpdateModalProps> = ({
    open,
    order,
    documents,
    onCancel,
    onSubmit,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRequests, setSelectedRequests] = useState<Record<number, QualityDocumentRequestType>>({});
    const [additionalDetails, setAdditionalDetails] = useState('');
    const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<string[]>([]);
    const [requestLoading, setRequestLoading] = useState(false);

    const filteredDocuments = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return documents;
        }

        return documents.filter(document =>
            [document.product_name, document.document_type, document.file_name].some(value =>
                value.toLowerCase().includes(query)
            )
        );
    }, [documents, searchQuery]);

    const groupedDocuments = useMemo(() => {
        return filteredDocuments.reduce<Record<string, { productName: string; documents: QualityDocumentRow[] }>>(
            (groups, document) => {
                const groupKey = String(document.product_zid);
                if (!groups[groupKey]) {
                    groups[groupKey] = {
                        productName: document.product_name,
                        documents: [],
                    };
                }
                groups[groupKey].documents.push(document);
                return groups;
            },
            {}
        );
    }, [filteredDocuments]);

    const selectedDocuments = documents.filter(document => selectedRequests[document.id]);
    const selectedCount = selectedDocuments.length;
    const canSend = selectedCount > 0;

    const reset = () => {
        setSearchQuery('');
        setSelectedRequests({});
        setAdditionalDetails('');
        setCollapsedGroupKeys([]);
    };

    const closeModal = () => {
        reset();
        onCancel();
    };

    const toggleProduct = (groupKey: string) => {
        setCollapsedGroupKeys(currentKeys =>
            currentKeys.includes(groupKey)
                ? currentKeys.filter(currentKey => currentKey !== groupKey)
                : [...currentKeys, groupKey]
        );
    };

    const toggleDocument = (document: QualityDocumentRow) => {
        setSelectedRequests(currentRequests => {
            const nextRequests = { ...currentRequests };
            if (nextRequests[document.id]) {
                delete nextRequests[document.id];
            } else {
                nextRequests[document.id] = getRequestTypeForDocument(document);
            }
            return nextRequests;
        });
    };

    const markAll = (requestType: QualityDocumentRequestType) => {
        setSelectedRequests(currentRequests => {
            const nextRequests = { ...currentRequests };
            filteredDocuments
                .filter(document => getRequestTypeForDocument(document) === requestType)
                .forEach(document => {
                    nextRequests[document.id] = requestType;
                });
            return nextRequests;
        });
    };

    const clearSelections = () => {
        setSelectedRequests({});
        setAdditionalDetails('');
    };

    const submitRequest = async () => {
        if (!canSend) {
            antdMessage.error('Select at least one document.');
            return;
        }

        setRequestLoading(true);
        try {
            const details = additionalDetails.trim();
            const payload: QualityDocumentUpdateRequestPayload = {
                document_requests: selectedDocuments.map(document => ({
                    document_id: document.id,
                    request_type: selectedRequests[document.id],
                })),
                ...(details ? { additional_details: details } : {}),
            };

            await onSubmit(payload);
            antdMessage.success('Your document help request has been sent successfully.');
            reset();
            onCancel();
        } catch (err: any) {
            antdMessage.error(err?.response?.data?.error || 'Unable to send document update request.');
        } finally {
            setRequestLoading(false);
        }
    };

    return (
        <Modal
            title="Request document help"
            open={open}
            width={720}
            confirmLoading={requestLoading}
            onCancel={closeModal}
            destroyOnClose
            bodyStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
            footer={[
                <Button key="cancel" onClick={closeModal}>
                    Cancel
                </Button>,
                <Button key="send" type="primary" loading={requestLoading} disabled={!canSend} onClick={submitRequest}>
                    Send request ({selectedCount})
                </Button>,
            ]}
        >
            <Paragraph>Request help from your sales representative by completing this form.</Paragraph>
            {order && (
                <div className="d-flex mb-3">
                    <div className="mr-5">
                        <Text type="secondary">Order</Text>
                        <div>{order.name}</div>
                    </div>
                    <div>
                        <Text type="secondary">Sales rep</Text>
                        <div>{order.owner?.fullname || order.owner?.email}</div>
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                    <Text strong>Select documents</Text>
                    <Tooltip
                        title={
                            <>
                                <div>Need update - document is missing or not ready</div>
                                <div>Need help - something looks wrong</div>
                            </>
                        }
                    >
                        <Button
                            type="link"
                            size="small"
                            className="ml-1"
                            aria-label="Document request option definitions"
                        >
                            What do these mean?
                        </Button>
                    </Tooltip>
                </div>
                <div>
                    <Text strong>{getSelectedCountLabel(selectedCount)}</Text>
                    {selectedCount > 0 && (
                        <Button type="link" onClick={clearSelections}>
                            Clear
                        </Button>
                    )}
                </div>
            </div>

            <div className="d-flex mb-3" style={{ gap: 8 }}>
                <Button onClick={() => markAll('update')}>Mark all: Need update</Button>
                <Button onClick={() => markAll('help')}>Mark all: Need help</Button>
            </div>

            <Input
                className="mb-3"
                placeholder="Search by product, document type, or filename"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
            />

            <div style={{ maxHeight: 320, overflowY: 'auto', borderTop: '1px solid #f0f0f0' }}>
                {Object.keys(groupedDocuments).length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents match your search." />
                )}
                {Object.entries(groupedDocuments).map(([groupKey, group]) => {
                    const expanded = !collapsedGroupKeys.includes(groupKey);

                    return (
                        <div key={groupKey} className="border-bottom">
                            <Button
                                type="text"
                                className="d-flex justify-content-between align-items-center w-100 px-0 py-2"
                                aria-expanded={expanded}
                                onClick={() => toggleProduct(groupKey)}
                            >
                                <Text strong>{group.productName}</Text>
                                <Text type="secondary">{expanded ? 'Collapse' : 'Expand'}</Text>
                            </Button>
                            {expanded &&
                                group.documents.map(document => {
                                    const requestType = getRequestTypeForDocument(document);
                                    const selected = selectedRequests[document.id] === requestType;

                                    return (
                                        <div
                                            key={document.id}
                                            className="d-flex justify-content-between align-items-center py-3"
                                            style={{
                                                background: selected ? '#f5f5f5' : undefined,
                                                borderLeft: selected ? '4px solid #111' : '4px solid transparent',
                                                paddingLeft: 12,
                                            }}
                                        >
                                            <div>
                                                <Text strong>{document.document_type}</Text>
                                                <div>
                                                    <Text type="secondary">{document.file_name}</Text>
                                                </div>
                                            </div>
                                            <Button
                                                type={selected ? 'primary' : 'default'}
                                                aria-pressed={selected}
                                                aria-label={`${getRequestTypeLabel(requestType)} for ${
                                                    document.file_name
                                                }`}
                                                onClick={() => toggleDocument(document)}
                                            >
                                                {getRequestTypeLabel(requestType)}
                                            </Button>
                                        </div>
                                    );
                                })}
                        </div>
                    );
                })}
            </div>

            {selectedCount > 0 && (
                <div className="mt-3">
                    <label htmlFor="quality-document-additional-details" className="d-block mb-1">
                        Additional details
                    </label>
                    <Input.TextArea
                        id="quality-document-additional-details"
                        rows={4}
                        maxLength={1000}
                        showCount
                        placeholder="Please add any other details you'd like us to know or take care of."
                        value={additionalDetails}
                        onChange={event => setAdditionalDetails(event.target.value)}
                    />
                </div>
            )}
        </Modal>
    );
};

export default RequestDocumentUpdateModal;
